import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  FilterSpecification,
  LightsSpecification,
  SymbolLayerSpecification
} from 'mapbox-gl';

export type StyleVariant = {
  importLayers: string[];
  overrides: {
    [layer: string]: {
      paint?: {light: {[attributes: string]: any}; dark: {[attributes: string]: any}};
      layout?: {[attributes: string]: any};
      base?: {[attributes: string]: any};
    };
  };
  fog: {
    light: {[attributes: string]: any};
    dark: {[attributes: string]: any};
  };
  lights: {
    light: LightsSpecification[];
    dark: LightsSpecification[];
  };
};

const lights: {light: LightsSpecification[]; dark: LightsSpecification[]} = {
  light: [
    {
      id: 'ambient',
      type: 'ambient',
      properties: {
        color: 'hsl(0, 0%, 100%)',
        intensity: 0.8
      }
    },
    {
      id: 'directional',
      type: 'directional',
      properties: {
        direction: ['literal', [180, 20]],
        color: 'hsl(0, 0%, 100%)',
        intensity: 0.2,
        // @ts-ignore
        'cast-shadows': true,
        'shadow-intensity': 1
      }
    }
  ],
  dark: [
    {
      id: 'ambient',
      type: 'ambient',
      properties: {
        color: 'hsl(217, 10%, 50%)',
        intensity: 0.3
      }
    },
    {
      id: 'directional',
      type: 'directional',
      properties: {
        direction: ['literal', [240, 80]],
        color: 'hsl(30, 98%, 76%)',
        intensity: ['interpolate', ['linear'], ['zoom'], 12, 0, 14, 0.3],
        // @ts-ignore
        'cast-shadows': true,
        'shadow-intensity': 1
      }
    }
  ]
};

const getLabelTextColor = (isDark: boolean, baseColor = 'white', darkColor = '#6f2d8e'): ExpressionSpecification => [
  'case',
  ['boolean', ['feature-state', 'hover'], false],
  isDark ? '#FFAA54' : '#DB7917',
  isDark ? baseColor : darkColor
];

const commonOverrides = {
  water: {
    paint: {
      light: {'fill-color': 'hsl(200, 100%, 75%)'},
      dark: {'fill-color': '#17285E'}
    }
  },
  'water-depth': {
    paint: {
      light: {
        'fill-color': [
          'interpolate',
          ['linear'],
          ['get', 'min_depth'],
          0,
          'rgba(123, 193, 230, 0.35)',
          200,
          'rgba(85, 182, 228, 0.35)',
          7000,
          'rgba(38, 162, 217, 0.4)'
        ]
      },
      dark: {
        'fill-color': [
          'interpolate',
          ['linear'],
          ['get', 'min_depth'],
          0,
          'rgba(123, 193, 230, 0.2)',
          200,
          'rgba(85, 182, 228, 0.2)',
          7000,
          'rgba(38, 162, 217, 0.2)'
        ]
      }
    }
  },
  landcover: {
    paint: {
      light: {
        'fill-color': [
          'match',
          ['get', 'class'],
          'wood',
          'hsla(119, 64%, 71%, 0.8)',
          'snow',
          'hsl(200, 70%, 90%)',
          [
            'interpolate',
            ['linear'],
            ['measure-light', 'brightness'],
            0.28,
            'hsl(137, 55%, 65%)',
            0.3,
            'hsl(115, 59%, 84%)'
          ]
        ],
        'fill-opacity': ['interpolate', ['exponential', 1.5], ['zoom'], 8, 0.7, 9, 0]
      },
      dark: {
        'fill-color': [
          'match',
          ['get', 'class'],
          'wood',
          'hsla(119, 64%, 71%, 0.6)',
          'snow',
          'hsl(200, 70%, 90%)',
          [
            'interpolate',
            ['linear'],
            ['measure-light', 'brightness'],
            0.28,
            'hsla(137, 55%, 65%, 0.8)',
            0.3,
            'hsl(115, 59%, 84%)'
          ]
        ],
        'fill-opacity': ['interpolate', ['exponential', 1.5], ['zoom'], 8, 0.7, 9, 0]
      }
    }
  },
  'admin-0-boundary': {
    paint: {
      light: {
        'line-color': 'rgba(159, 173, 160, 1)',
        'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.6, 12, 4.2]
      },
      dark: {
        'line-color': 'rgba(159, 173, 160, 1)',
        'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.6, 12, 4.2]
      }
    }
  }
};

const fog = {
  light: {
    'high-color': 'hsla(200, 100%, 75%, 0.4)',
    color: 'hsla(200, 100%, 75%, 0.4)',
    'space-color': 'rgb(250,250,250)',
    'horizon-blend': ['interpolate', ['linear'], ['zoom'], 4, 0.1, 7, 0.1],
    'star-intensity': 0
  },
  dark: {
    'high-color': 'rgba(242, 247, 255, 0.2)',
    color: 'hsla(217, 30%, 50%, 0.7)',
    'space-color': ['interpolate', ['linear'], ['zoom'], 0, 'rgb(12, 5, 15)', 10, '#292130'],
    'horizon-blend': ['interpolate', ['linear'], ['zoom'], 3, 0.01, 10, 0.04],
    'star-intensity': 0.3
  }
};

type StyleType = {
  countries: StyleVariant;
  places: StyleVariant;
};

const mapStyles: StyleType = {
  countries: {
    importLayers: [
      'land',
      'landcover',
      'landuse',
      'bottom',
      '3d-hillshade',
      'hillshade',
      'water',
      'water-depth',
      'admin-0-boundary'
    ],
    overrides: {
      'country-label': {
        paint: {
          light: {
            'text-color': 'white',
            'text-halo-color': 'rgba(71, 9, 101, 0.8)',
            'text-halo-width': 1.2
          },
          dark: {
            'text-halo-color': 'hsla(0, 0%, 0%, 0.4)',
            'text-halo-width': 1.2
          }
        }
      },
      ...commonOverrides
    },
    fog,
    lights
  },
  places: {
    importLayers: [
      'land',
      'landcover',
      'landuse',
      'bottom',
      '3d-hillshade',
      'hillshade',
      'water-shadow',
      'waterway',
      'water',
      'water-depth',
      'land-structure-polygon',
      'land-structure-line',
      'aeroway-polygon',
      'aeroway-line',
      'tunnel-street-case',
      'tunnel-primary-case',
      'tunnel-major-link-case',
      'tunnel-motorway-trunk-case',
      'tunnel-major-link',
      'tunnel-street',
      'tunnel-motorway-trunk',
      'tunnel-primary',
      'road-path-case',
      'road-steps-case',
      'road-pedestrian-case',
      'road-path-trail',
      'road-path',
      'road-steps',
      'road-pedestrian',
      'golf-hole-line',
      'gate-fence-hedge-shade',
      'tunnel-path-trail',
      'tunnel-path',
      'gate-fence-hedge',
      'road-minor-case',
      'road-street-case',
      'road-minor-link-case',
      'road-secondary-tertiary-case',
      'road-primary-case',
      'road-major-link-case',
      'road-motorway-trunk-case',
      'road-minor',
      'road-minor-link',
      'road-major-link',
      'road-street',
      'road-street-low',
      'road-secondary-tertiary',
      'road-primary',
      'road-motorway-trunk',
      'road-path-cycleway-piste',
      'road-rail',
      'road-rail-tracks',
      'crosswalks',
      'bridge-path-case',
      'bridge-steps-case',
      'bridge-pedestrian-case',
      'bridge-path-trail',
      'bridge-path',
      'bridge-steps',
      'bridge-pedestrian',
      'bridge-minor-shadow',
      'bridge-minor-case',
      'bridge-street-shadow',
      'bridge-street-case',
      'bridge-minor-link-shadow',
      'bridge-minor-link-case',
      'bridge-secondary-tertiary-shadow',
      'bridge-secondary-tertiary-case',
      'bridge-primary-shadow',
      'bridge-primary-case',
      'bridge-major-link-shadow',
      'bridge-major-link-case',
      'bridge-motorway-trunk-shadow',
      'bridge-motorway-trunk-case',
      'bridge-minor',
      'bridge-minor-link',
      'bridge-major-link',
      'bridge-street',
      'bridge-street-low',
      'bridge-secondary-tertiary',
      'bridge-primary',
      'bridge-motorway-trunk',
      'bridge-major-link-2-shadow',
      'bridge-major-link-2-case',
      'bridge-motorway-trunk-2-shadow',
      'bridge-motorway-trunk-2-case',
      'bridge-major-link-2',
      'bridge-motorway-trunk-2',
      'bridge-path-cycleway-piste',
      'bridge-rail',
      'bridge-rail-tracks',
      'aerialway',
      'admin-0-boundary-bg',
      'admin-0-boundary',
      'middle',
      'trees',
      'wind-turbine-towers',
      'wind-turbine-rotors',
      'road-label',
      'path-pedestrian-label',
      '3d-building',
      'building-models',
      'natural-line-label',
      'natural-point-label',
      'water-line-label',
      'waterway-label',
      'water-point-label',
      'road-intersection',
      'top',
      'transit-label',
      'airport-label',
      'settlement-subdivision-label',
      'state-label'
    ],
    overrides: {
      'settlement-minor-label': {layout: {'icon-image': ''}},
      'settlement-major-label': {layout: {'icon-image': ''}},
      ...commonOverrides,
      'road-motorway-trunk': {
        base: {minzoom: 5},
        paint: {
          light: {
            'line-width': [
              'interpolate',
              ['exponential', 1.5],
              ['zoom'],
              5,
              ['match', ['get', 'class'], ['motorway', 'trunk', 'primary'], 0.3, ['secondary', 'tertiary'], 0.12, 0],
              13,
              [
                'match',
                ['get', 'class'],
                ['motorway', 'trunk', 'primary'],
                4,
                ['secondary', 'tertiary'],
                3,
                ['motorway_link', 'trunk_link', 'primary_link', 'street', 'street_limited'],
                1.2,
                0.6
              ],
              18,
              [
                'match',
                ['get', 'class'],
                ['motorway', 'trunk', 'primary'],
                38.4,
                ['secondary', 'tertiary'],
                31.2,
                ['motorway_link', 'trunk_link', 'primary_link', 'street', 'street_limited'],
                21.6,
                12
              ],
              22,
              [
                'match',
                ['get', 'class'],
                ['motorway', 'trunk', 'primary'],
                384,
                ['secondary', 'tertiary'],
                312,
                ['motorway_link', 'trunk_link', 'primary_link', 'street', 'street_limited'],
                216,
                120
              ]
            ]
          },
          dark: {}
        }
      }
    },
    fog,
    lights
  }
};

export const getHumboLayers = (
  isDark: boolean
): [SymbolLayerSpecification, SymbolLayerSpecification, SymbolLayerSpecification, SymbolLayerSpecification] => [
  {
    id: 'settlement-minor-label',
    type: 'symbol',
    source: 'place_label',
    'source-layer': 'place_label',
    minzoom: 2,
    maxzoom: 13,
    filter: [
      'all',
      ['<=', ['get', 'filterrank'], 3],
      [
        'match',
        ['get', 'class'],
        ['settlement', 'disputed_settlement'],
        ['match', ['get', 'worldview'], ['all', 'US'], true, false],
        false
      ],
      [
        'step',
        ['zoom'],
        ['>', ['get', 'symbolrank'], 6],
        4,
        ['>=', ['get', 'symbolrank'], 7],
        6,
        ['>=', ['get', 'symbolrank'], 8],
        7,
        ['>=', ['get', 'symbolrank'], 10],
        10,
        ['>=', ['get', 'symbolrank'], 11],
        11,
        ['>=', ['get', 'symbolrank'], 13],
        12,
        ['>=', ['get', 'symbolrank'], 15]
      ],
      ['case', ['<=', ['pitch'], 45], true, ['<=', ['distance-from-center'], 2]]
    ] as FilterSpecification,
    layout: {
      'text-line-height': 1.1,
      'text-size': [
        'interpolate',
        ['cubic-bezier', 0.2, 0, 0.9, 1],
        ['zoom'],
        3,
        ['step', ['get', 'symbolrank'], 11, 9, 10],
        6,
        ['step', ['get', 'symbolrank'], 14, 9, 12, 12, 10],
        8,
        ['step', ['get', 'symbolrank'], 16, 9, 14, 12, 12, 15, 10],
        13,
        ['step', ['get', 'symbolrank'], 22, 9, 20, 12, 16, 15, 14]
      ],
      'text-radial-offset': ['step', ['zoom'], ['match', ['get', 'capital'], 2, 0.6, 0.55], 8, 0],
      'symbol-sort-key': ['get', 'symbolrank'],
      'text-font': ['Nunito Regular'],
      'text-justify': 'auto',
      'text-anchor': ['step', ['zoom'], ['get', 'text_anchor'], 8, 'center'],
      'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
      'text-max-width': 7
    },
    paint: {
      'text-color': getLabelTextColor(isDark, 'white', 'hsl(0, 0%, 0%)'),
      'text-halo-color': [
        'interpolate',
        ['linear'],
        ['measure-light', 'brightness'],
        0.25,
        'hsla(0, 0%, 0%, 0.8)',
        0.3,
        'hsl(0, 0%, 100%)'
      ],
      'text-halo-width': 1,
      'text-halo-blur': 1
    }
  },
  {
    id: 'settlement-major-label',
    type: 'symbol',
    source: 'place_label',
    'source-layer': 'place_label',
    minzoom: 2,
    maxzoom: 15,
    filter: [
      'all',
      ['<=', ['get', 'filterrank'], 3],
      [
        'match',
        ['get', 'class'],
        ['settlement', 'disputed_settlement'],
        ['match', ['get', 'worldview'], ['all', 'US'], true, false],
        false
      ],
      [
        'step',
        ['zoom'],
        false,
        2,
        ['<=', ['get', 'symbolrank'], 6],
        4,
        ['<', ['get', 'symbolrank'], 7],
        6,
        ['<', ['get', 'symbolrank'], 8],
        7,
        ['<', ['get', 'symbolrank'], 10],
        10,
        ['<', ['get', 'symbolrank'], 11],
        11,
        ['<', ['get', 'symbolrank'], 13],
        12,
        ['<', ['get', 'symbolrank'], 15],
        13,
        ['>=', ['get', 'symbolrank'], 11],
        14,
        ['>=', ['get', 'symbolrank'], 15]
      ],
      ['case', ['<=', ['pitch'], 45], true, ['<=', ['distance-from-center'], 2]]
    ] as FilterSpecification,
    layout: {
      'text-line-height': 1.1,
      'text-size': [
        'interpolate',
        ['cubic-bezier', 0.2, 0, 0.9, 1],
        ['zoom'],
        3,
        ['step', ['get', 'symbolrank'], 13, 6, 11],
        6,
        ['step', ['get', 'symbolrank'], 18, 6, 16, 7, 14],
        8,
        ['step', ['get', 'symbolrank'], 20, 9, 16, 10, 14],
        15,
        ['step', ['get', 'symbolrank'], 24, 9, 20, 12, 16, 15, 14]
      ],
      'text-radial-offset': ['step', ['zoom'], ['match', ['get', 'capital'], 2, 0.6, 0.55], 8, 0],
      'symbol-sort-key': ['get', 'symbolrank'],
      'text-font': ['Nunito SemiBold'],
      'text-justify': [
        'step',
        ['zoom'],
        [
          'match',
          ['get', 'text_anchor'],
          ['left', 'bottom-left', 'top-left'],
          'left',
          ['right', 'bottom-right', 'top-right'],
          'right',
          'center'
        ],
        8,
        'center'
      ],
      'text-anchor': ['step', ['zoom'], ['get', 'text_anchor'], 8, 'center'],
      'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
      'text-max-width': 7
    },
    paint: {
      'text-color': getLabelTextColor(isDark, 'white', 'hsl(0, 0%, 0%)'),
      'text-halo-color': [
        'interpolate',
        ['linear'],
        ['measure-light', 'brightness'],
        0.25,
        'hsl(0, 0%, 0%)',
        0.3,
        'hsl(0, 0%, 100%)'
      ],
      'text-halo-width': 1,
      'text-halo-blur': 1
    }
  },
  {
    id: 'country-label',
    type: 'symbol',
    source: 'place_label',
    'source-layer': 'place_label',
    minzoom: 1,
    maxzoom: 10,
    filter: [
      'all',
      [
        'match',
        ['get', 'class'],
        ['country', 'disputed_country'],
        ['match', ['get', 'worldview'], ['all', 'US'], true, false],
        false
      ],
      ['case', ['<=', ['pitch'], 45], true, ['<=', ['distance-from-center'], 2]]
    ] as FilterSpecification,
    layout: {
      'icon-image': '',
      'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
      'text-line-height': 1.1,
      'text-max-width': 6,
      'text-font': ['Nunito SemiBold'],
      'text-radial-offset': ['step', ['zoom'], 0.6, 8, 0],
      'text-justify': [
        'step',
        ['zoom'],
        [
          'match',
          ['get', 'text_anchor'],
          ['left', 'bottom-left', 'top-left'],
          'left',
          ['right', 'bottom-right', 'top-right'],
          'right',
          'center'
        ],
        7,
        'auto'
      ],
      'text-size': [
        'interpolate',
        ['cubic-bezier', 0.2, 0, 0.7, 1],
        ['zoom'],
        1,
        ['step', ['get', 'symbolrank'], 11, 4, 9, 5, 8],
        9,
        ['step', ['get', 'symbolrank'], 22, 4, 19, 5, 17]
      ]
    },
    paint: {
      'text-color': [
        'interpolate',
        ['linear'],
        ['measure-light', 'brightness'],
        0.28,
        'hsl(0, 0%, 100%)',
        0.3,
        'hsl(0, 0%, 0%)'
      ],
      'text-halo-color': [
        'interpolate',
        ['linear'],
        ['measure-light', 'brightness'],
        0.25,
        'hsl(0, 0%, 0%)',
        0.3,
        'hsl(0, 0%, 100%)'
      ],
      'text-halo-width': 1.25
    }
  },
  {
    id: 'poi-label',
    type: 'symbol',
    source: 'place_label',
    'source-layer': 'poi_label',
    minzoom: 6,
    filter: ['==', ['get', 'class'], 'park_like'],
    layout: {
      'text-size': [
        'step',
        ['zoom'],
        ['step', ['number', ['get', 'sizerank']], 18, 5, 13],
        17,
        ['step', ['number', ['get', 'sizerank']], 18, 13, 13]
      ],
      'text-field': ['format', ['coalesce', ['get', 'name_en'], ['get', 'name']], {}],
      'text-font': ['Nunito SemiBold']
    },
    paint: {
      'text-halo-width': 1,
      'text-halo-color': [
        'interpolate',
        ['linear'],
        ['measure-light', 'brightness'],
        0.25,
        'hsl(0, 0%, 10%)',
        0.3,
        'hsl(0, 0%, 100%)'
      ],
      'text-color': [
        'match',
        ['get', 'class'],
        'park_like',
        getLabelTextColor(isDark, 'hsl(110, 55%, 65%)', 'hsl(110, 85%, 25%)'),
        [
          'interpolate',
          ['linear'],
          ['measure-light', 'brightness'],
          0.25,
          'hsl(210, 20%, 70%)',
          0.3,
          'hsl(210, 20%, 43%)'
        ]
      ]
    }
  }
];

export const getPinsLayers = (
  isDark: boolean
): [
  CircleLayerSpecification,
  CircleLayerSpecification,
  SymbolLayerSpecification,
  SymbolLayerSpecification,
  SymbolLayerSpecification
] => [
  {
    id: 'clusteredPointsGlow',
    type: 'circle',
    source: 'places',
    filter: ['has', 'point_count'],
    paint: {
      'circle-pitch-alignment': 'viewport',
      'circle-color': isDark ? 'rgb(141, 95, 163)' : 'rgba(101, 39, 131, 1)',
      'circle-radius': ['step', ['get', 'point_count'], 16, 9, 18, 21, 22, 100, 26, 1000, 40],
      'circle-emissive-strength': 1,
      'circle-opacity': isDark ? 0.45 : 0.15,
      'circle-stroke-width': 0
    }
  },
  {
    id: 'clusteredPoints',
    type: 'circle',
    source: 'places',
    filter: ['has', 'point_count'],
    paint: {
      'circle-pitch-alignment': 'viewport',
      'circle-color': isDark ? 'rgba(142, 29, 197, 1)' : 'rgba(101, 39, 131, 1)',
      'circle-emissive-strength': 1,
      'circle-radius': ['step', ['get', 'point_count'], 10, 9, 12, 21, 16, 100, 20, 1000, 40],
      'circle-opacity': 1,
      'circle-stroke-width': 2,
      'circle-stroke-color': isDark ? 'rgb(161, 37, 223)' : 'rgb(141, 95, 163)',
      'circle-stroke-opacity': 1
    }
  },
  {
    id: 'pointCount',
    type: 'symbol',
    source: 'places',
    filter: ['has', 'point_count'],
    paint: {'text-color': 'white'},
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-size': 12,
      'text-allow-overlap': true,
      'text-font': ['Nunito Bold'],
      'text-anchor': 'center',
      'text-offset': [0, 0.03]
    }
  },
  {
    id: 'singlePointPin',
    type: 'symbol',
    source: 'places',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'icon-opacity': 1,
      'text-color': getLabelTextColor(isDark),
      'text-halo-color': isDark ? 'black' : 'rgba(255,255,255,0.6)',
      'text-halo-width': 1.2
    },
    layout: {
      'icon-optional': false,
      'text-field': ['get', 'name'],
      'icon-image': isDark ? '{icon}_dark' : '{icon}',
      'icon-anchor': 'top',
      'icon-offset': [0, -26],
      'icon-size': 0.7,
      'text-size': 13,
      'text-font': ['Nunito SemiBold'],
      'text-pitch-alignment': 'viewport',
      'text-anchor': 'top',
      'text-padding': 0,
      'text-offset': [0, 0.3],
      'text-max-width': 6,
      'text-allow-overlap': false,
      'text-line-height': 1
    }
  },
  {
    id: 'selectedPointPin',
    type: 'symbol',
    source: 'selectedPlaces',
    paint: {
      'icon-opacity': 1,
      'text-color': getLabelTextColor(isDark),
      'text-halo-color': isDark ? 'black' : 'rgba(255,255,255,0.6)',
      'text-halo-width': 1.2
    },
    layout: {
      'icon-optional': false,
      'text-field': ['get', 'name'],
      'icon-image': isDark ? '{icon}_dark' : '{icon}',
      'icon-anchor': 'top',
      'icon-offset': [0, -40],
      'icon-size': 1,
      'text-size': 13,
      'text-font': ['Nunito SemiBold'],
      'text-pitch-alignment': 'viewport',
      'text-anchor': 'top',
      'text-padding': 0,
      'text-offset': [0, 0.3],
      'text-max-width': 6,
      'text-allow-overlap': false,
      'text-line-height': 1
    }
  }
];

export default mapStyles;
